<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useEditorStore } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';

const editorStore = useEditorStore();
const assetsStore = useAssetsStore();

// Spritesheet viewer state
const spritesheetImage = ref<string | null>(null);
const spritesheetSize = ref<{ width: number; height: number }>({ width: 0, height: 0 });
const hoveredTile = ref<{ x: number; y: number } | null>(null);
const loadingSprite = ref(false);

// Drag selection state
const isDragging = ref(false);
const dragStart = ref<{ x: number; y: number } | null>(null);
const dragEnd = ref<{ x: number; y: number } | null>(null);
const dragThreshold = 5;
const dragButton = ref<number>(0);

// Get spritesheet image path for selected sprite
function getSpritesheetPath(spriteId: number): string | null {
    const entry = assetsStore.getAtlasEntry(spriteId, 0);
    if (entry) {
        return `assets/${entry.image}`;
    }
    return null;
}

// Load and display the spritesheet for a sprite
async function loadSpritesheet(spriteId: number): Promise<void> {
    loadingSprite.value = true;

    try {
        await assetsStore.loadSpritesForIds(new Set([spriteId]));

        const imagePath = getSpritesheetPath(spriteId);
        if (imagePath) {
            spritesheetImage.value = imagePath;

            const img = new Image();
            img.onload = () => {
                spritesheetSize.value = { width: img.width, height: img.height };
            };
            img.src = imagePath;
        }
    } finally {
        loadingSprite.value = false;
    }
}

// Watch for sprite selection changes
watch(() => editorStore.selectedPaletteSpriteId, async (spriteId) => {
    if (spriteId !== null) {
        await loadSpritesheet(spriteId);
    } else {
        spritesheetImage.value = null;
        spritesheetSize.value = { width: 0, height: 0 };
    }
}, { immediate: true });

// Calculate tiles per row for the current spritesheet
const tilesPerRow = computed(() => {
    if (spritesheetSize.value.width === 0) return 1;
    return Math.floor(spritesheetSize.value.width / 32);
});

// Toggle a tile in/out of the brush
function toggleTileAt(tileX: number, tileY: number): void {
    if (editorStore.selectedPaletteSpriteId === null) return;

    const frame = tileY * tilesPerRow.value + tileX;

    const entry = assetsStore.getAtlasEntry(editorStore.selectedPaletteSpriteId, frame);
    if (!entry) return;

    // Calculate relative offset based on existing brush anchor (first tile)
    const brush = editorStore.paletteTab === 'tiles' ? editorStore.tileBrush : editorStore.objectBrush;
    let relativeX = 0;
    let relativeY = 0;

    if (brush.length > 0) {
        // Find the anchor tile (tile with smallest frame in the same sprite)
        const sameSpriteItems = brush.filter(t => t.spriteId === editorStore.selectedPaletteSpriteId);
        if (sameSpriteItems.length > 0) {
            const anchorTile = sameSpriteItems.reduce((min, t) => t.frame < min.frame ? t : min);
            const anchorX = anchorTile.frame % tilesPerRow.value;
            const anchorY = Math.floor(anchorTile.frame / tilesPerRow.value);
            // Calculate offset relative to anchor, then add anchor's current offset
            relativeX = (tileX - anchorX) + anchorTile.offsetX;
            relativeY = (tileY - anchorY) + anchorTile.offsetY;
        }
    }

    if (editorStore.paletteTab === 'tiles') {
        editorStore.toggleTileBrush(relativeX, relativeY, editorStore.selectedPaletteSpriteId, frame);
    } else {
        editorStore.toggleObjectBrush(relativeX, relativeY, editorStore.selectedPaletteSpriteId, frame);
    }
}

// Add all tiles in a rectangle to the brush
function addRectangleToBrush(x1: number, y1: number, x2: number, y2: number): void {
    if (editorStore.selectedPaletteSpriteId === null) return;

    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const brush = editorStore.paletteTab === 'tiles' ? editorStore.tileBrush : editorStore.objectBrush;

    // Find the anchor tile from existing brush (if any) to calculate proper offsets
    let anchorX = minX;
    let anchorY = minY;
    let anchorOffsetX = 0;
    let anchorOffsetY = 0;

    const sameSpriteItems = brush.filter(t => t.spriteId === editorStore.selectedPaletteSpriteId);
    if (sameSpriteItems.length > 0) {
        // Use existing brush's anchor
        const anchorTile = sameSpriteItems.reduce((min, t) => t.frame < min.frame ? t : min);
        anchorX = anchorTile.frame % tilesPerRow.value;
        anchorY = Math.floor(anchorTile.frame / tilesPerRow.value);
        anchorOffsetX = anchorTile.offsetX;
        anchorOffsetY = anchorTile.offsetY;
    }

    for (let tileY = minY; tileY <= maxY; tileY++) {
        for (let tileX = minX; tileX <= maxX; tileX++) {
            const frame = tileY * tilesPerRow.value + tileX;
            const entry = assetsStore.getAtlasEntry(editorStore.selectedPaletteSpriteId, frame);
            if (entry) {
                // Calculate offset relative to the anchor tile
                const relativeX = (tileX - anchorX) + anchorOffsetX;
                const relativeY = (tileY - anchorY) + anchorOffsetY;

                if (editorStore.paletteTab === 'tiles') {
                    if (!editorStore.tileBrush.some(t => t.spriteId === editorStore.selectedPaletteSpriteId && t.frame === frame)) {
                        editorStore.toggleTileBrush(relativeX, relativeY, editorStore.selectedPaletteSpriteId, frame);
                    }
                } else {
                    if (!editorStore.objectBrush.some(t => t.spriteId === editorStore.selectedPaletteSpriteId && t.frame === frame)) {
                        editorStore.toggleObjectBrush(relativeX, relativeY, editorStore.selectedPaletteSpriteId, frame);
                    }
                }
            }
        }
    }
}

// Remove all tiles in a rectangle from the brush
function removeRectangleFromBrush(x1: number, y1: number, x2: number, y2: number): void {
    if (editorStore.selectedPaletteSpriteId === null) return;

    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    for (let tileY = minY; tileY <= maxY; tileY++) {
        for (let tileX = minX; tileX <= maxX; tileX++) {
            const frame = tileY * tilesPerRow.value + tileX;
            if (editorStore.paletteTab === 'tiles') {
                if (editorStore.tileBrush.some(t => t.spriteId === editorStore.selectedPaletteSpriteId && t.frame === frame)) {
                    editorStore.toggleTileBrush(tileX, tileY, editorStore.selectedPaletteSpriteId, frame);
                }
            } else {
                if (editorStore.objectBrush.some(t => t.spriteId === editorStore.selectedPaletteSpriteId && t.frame === frame)) {
                    editorStore.toggleObjectBrush(tileX, tileY, editorStore.selectedPaletteSpriteId, frame);
                }
            }
        }
    }
}

// Get pixel coords relative to spritesheet
function getSpritesheetCoords(e: MouseEvent): { x: number; y: number } {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    return {
        x: e.clientX - rect.left + target.scrollLeft,
        y: e.clientY - rect.top + target.scrollTop,
    };
}

// Mouse down - start potential drag
function handleSpritesheetMouseDown(e: MouseEvent): void {
    if (e.button !== 0 && e.button !== 2) return;

    if (e.button === 2) {
        e.preventDefault();
    }

    const coords = getSpritesheetCoords(e);
    dragStart.value = { x: coords.x, y: coords.y };
    dragEnd.value = { x: coords.x, y: coords.y };
    isDragging.value = false;
    dragButton.value = e.button;

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
}

// Mouse move during drag
function handleDragMove(e: MouseEvent): void {
    if (!dragStart.value) return;

    const container = document.querySelector('.spritesheet-container') as HTMLElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left + container.scrollLeft;
    const y = e.clientY - rect.top + container.scrollTop;

    const dx = Math.abs(x - dragStart.value.x);
    const dy = Math.abs(y - dragStart.value.y);
    if (dx > dragThreshold || dy > dragThreshold) {
        isDragging.value = true;
    }

    dragEnd.value = { x, y };
}

// Mouse up - end drag or single click
function handleDragEnd(_e: MouseEvent): void {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);

    if (!dragStart.value || !dragEnd.value) {
        dragStart.value = null;
        dragEnd.value = null;
        isDragging.value = false;
        return;
    }

    const isRightClick = dragButton.value === 2;

    if (isDragging.value) {
        const startTileX = Math.floor(dragStart.value.x / 32);
        const startTileY = Math.floor(dragStart.value.y / 32);
        const endTileX = Math.floor(dragEnd.value.x / 32);
        const endTileY = Math.floor(dragEnd.value.y / 32);

        if (isRightClick) {
            removeRectangleFromBrush(startTileX, startTileY, endTileX, endTileY);
        } else {
            addRectangleToBrush(startTileX, startTileY, endTileX, endTileY);
        }
    } else {
        const tileX = Math.floor(dragStart.value.x / 32);
        const tileY = Math.floor(dragStart.value.y / 32);
        toggleTileAt(tileX, tileY);
    }

    dragStart.value = null;
    dragEnd.value = null;
    isDragging.value = false;
}

function handleSpritesheetMouseMove(e: MouseEvent): void {
    const coords = getSpritesheetCoords(e);

    const tileX = Math.floor(coords.x / 32);
    const tileY = Math.floor(coords.y / 32);

    if (tileX >= 0 && tileY >= 0 &&
        tileX * 32 < spritesheetSize.value.width &&
        tileY * 32 < spritesheetSize.value.height) {
        hoveredTile.value = { x: tileX, y: tileY };
    } else {
        hoveredTile.value = null;
    }
}

function handleSpritesheetMouseLeave(): void {
    hoveredTile.value = null;
}

// Computed for drag selection rectangle
const dragSelectionRect = computed(() => {
    if (!isDragging.value || !dragStart.value || !dragEnd.value) return null;

    const startTileX = Math.floor(dragStart.value.x / 32);
    const startTileY = Math.floor(dragStart.value.y / 32);
    const endTileX = Math.floor(dragEnd.value.x / 32);
    const endTileY = Math.floor(dragEnd.value.y / 32);

    const minTileX = Math.min(startTileX, endTileX);
    const maxTileX = Math.max(startTileX, endTileX);
    const minTileY = Math.min(startTileY, endTileY);
    const maxTileY = Math.max(startTileY, endTileY);

    return {
        left: minTileX * 32,
        top: minTileY * 32,
        width: (maxTileX - minTileX + 1) * 32,
        height: (maxTileY - minTileY + 1) * 32,
        isDeselect: dragButton.value === 2,
    };
});

// Get selected tiles for rendering overlay
const selectedTilesInViewer = computed(() => {
    if (editorStore.selectedPaletteSpriteId === null) return [];

    const brush = editorStore.paletteTab === 'tiles' ? editorStore.tileBrush : editorStore.objectBrush;
    const result: Array<{ x: number; y: number }> = [];

    for (const tile of brush) {
        if (tile.spriteId === editorStore.selectedPaletteSpriteId) {
            const tileX = tile.frame % tilesPerRow.value;
            const tileY = Math.floor(tile.frame / tilesPerRow.value);
            result.push({ x: tileX, y: tileY });
        }
    }

    return result;
});

// Clear brush
function clearBrush(): void {
    if (editorStore.paletteTab === 'tiles') {
        editorStore.clearTileBrush();
    } else {
        editorStore.clearObjectBrush();
    }
}

// Current brush info
const currentBrush = computed(() => {
    return editorStore.paletteTab === 'tiles' ? editorStore.tileBrush : editorStore.objectBrush;
});

const brushBounds = computed(() => {
    return editorStore.paletteTab === 'tiles' ? editorStore.tileBrushBounds : editorStore.objectBrushBounds;
});

// Get the number of frames for the selected sprite
const frameCount = computed(() => {
    if (editorStore.selectedPaletteSpriteId === null) return 0;
    return assetsStore.getFramesForSprite(editorStore.selectedPaletteSpriteId).length;
});

// Cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
});
</script>

<template>
    <div
        class="spritesheet-viewer"
        v-if="editorStore.selectedPaletteSpriteId !== null"
    >
        <div class="viewer-header">
            <span class="viewer-title">Sprite {{ editorStore.selectedPaletteSpriteId }}</span>
            <span class="frame-count">({{ frameCount }} frames)</span>
        </div>

        <div v-if="loadingSprite" class="loading-indicator">
            Loading...
        </div>

        <div
            v-else-if="spritesheetImage"
            class="spritesheet-container"
            @mousedown="handleSpritesheetMouseDown"
            @mousemove="handleSpritesheetMouseMove"
            @mouseleave="handleSpritesheetMouseLeave"
            @contextmenu.prevent
        >
            <img
                :src="spritesheetImage"
                class="spritesheet-image"
                draggable="false"
            />
            <!-- Grid overlay -->
            <div class="grid-overlay" :style="{
                width: spritesheetSize.width + 'px',
                height: spritesheetSize.height + 'px',
            }">
                <!-- Selected tiles in brush -->
                <div
                    v-for="(tile, index) in selectedTilesInViewer"
                    :key="'selected-' + index"
                    class="tile-selected"
                    :style="{
                        left: tile.x * 32 + 'px',
                        top: tile.y * 32 + 'px',
                    }"
                ></div>
                <!-- Drag selection rectangle -->
                <div
                    v-if="dragSelectionRect"
                    class="drag-selection"
                    :class="{ deselect: dragSelectionRect.isDeselect }"
                    :style="{
                        left: dragSelectionRect.left + 'px',
                        top: dragSelectionRect.top + 'px',
                        width: dragSelectionRect.width + 'px',
                        height: dragSelectionRect.height + 'px',
                    }"
                ></div>
                <!-- Hover highlight -->
                <div
                    v-if="hoveredTile && !isDragging"
                    class="tile-hover"
                    :style="{
                        left: hoveredTile.x * 32 + 'px',
                        top: hoveredTile.y * 32 + 'px',
                    }"
                ></div>
            </div>
        </div>

        <!-- Selection info footer -->
        <div class="selection-info">
            <div class="brush-info">
                <span>Brush: {{ currentBrush.length }} tile{{ currentBrush.length !== 1 ? 's' : '' }}</span>
                <span v-if="currentBrush.length > 0" class="brush-size">
                    ({{ brushBounds.width }}x{{ brushBounds.height }})
                </span>
            </div>
            <button
                v-if="currentBrush.length > 0"
                class="clear-btn"
                @click="clearBrush"
                title="Clear brush selection"
            >
                Clear
            </button>
        </div>

    </div>
    <div v-else class="no-selection">
        <p>Select a sprite from the palette to view its tiles</p>
    </div>
</template>

<style scoped>
.spritesheet-viewer {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg-panel);
}

.viewer-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.viewer-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.frame-count {
    font-weight: normal;
    text-transform: none;
    white-space: nowrap;
}

.loading-indicator {
    padding: 16px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
}

.spritesheet-container {
    flex: 1;
    overflow: auto;
    position: relative;
    cursor: crosshair;
    background:
        linear-gradient(45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(-45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #2a2a3a 75%),
        linear-gradient(-45deg, transparent 75%, #2a2a3a 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
    background-color: #1e1e2e;
}

.spritesheet-image {
    display: block;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
}

.grid-overlay {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image:
        linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 32px 32px;
}

.tile-selected {
    position: absolute;
    width: 32px;
    height: 32px;
    border: 2px solid #22c55e;
    background: rgba(34, 197, 94, 0.3);
    box-sizing: border-box;
}

.drag-selection {
    position: absolute;
    border: 2px dashed #f59e0b;
    background: rgba(245, 158, 11, 0.2);
    box-sizing: border-box;
    pointer-events: none;
}

.drag-selection.deselect {
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.2);
}

.tile-hover {
    position: absolute;
    width: 32px;
    height: 32px;
    border: 2px solid var(--accent-color);
    background: rgba(99, 102, 241, 0.3);
    box-sizing: border-box;
}

.selection-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-color);
    font-size: 11px;
    color: var(--text-secondary);
    flex-shrink: 0;
}

.brush-info {
    display: flex;
    gap: 8px;
}

.brush-size {
    color: var(--text-secondary);
}

.clear-btn {
    padding: 4px 8px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.clear-btn:hover {
    background: #ef4444;
    border-color: #ef4444;
    color: white;
}

.no-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 16px;
    color: var(--text-secondary);
    font-size: 12px;
    text-align: center;
}
</style>
